import type * as Points from "../../lib/models/points.server.ts"
import type * as Referrals from "../../lib/models/referrals.server.ts"

import type { ShopifyProductWithData } from "../../lib/models/shopify-product.server.ts"

import * as UI from "@iyk/ui"
import * as React from "react"
import * as Shopify from "../../lib/shopify/shopify.ts"
import * as Dialog from "../../lib/ui/dialog.tsx"

import { toSentenceCase, toTemplate } from "@iyk/string"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { useLang } from "../../lib/lang/use-lang.ts"
import { formatNumber } from "../../lib/utils/number.ts"

export function ReferrerDialog({
  item,
  shopifyProduct,
  referrerData,
  pointsValueMapping,
}: ReferrerDialogProps) {
  const lang = useLang()
  const { iykUser } = useIykUser()
  const [isCopied, setIsCopied] = React.useState(false)

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referrerData.shareLink)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      console.error("Failed to copy:", error)
    }
  }

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          // We use only the title otherwise the "text" will be below the url
          title: generateShareText(lang, iykUser?.username ?? "", item.name, referrerData),
          url: referrerData.shareLink,
        })
      } catch (error) {
        console.error("Failed to share", error)
      }
    } else {
      console.error("Sharing functionality is not available in this browser.")
    }
  }

  const pointsQuantity = calculatePointsQuantity({
    discountPercentageDecimal: referrerData.discountPercentageDecimal,
    pointsPerUsdReferral: pointsValueMapping?.pointsPerUsdReferral ?? 0,
    productPriceAmount: Shopify.getDefaultVariant(shopifyProduct.shopifyProductData.variants.nodes)
      .price.amount,
  })

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <UI.Button type="button" size="lg" kind="outline" className="w-full">
          <UI.Icon.Team />
          <span>{toSentenceCase(lang.terms.REFER_AND_EARN_POINTS)}</span>
        </UI.Button>
      </Dialog.Trigger>
      <Dialog.Content className="py-6">
        <div className="sr-only">
          <Dialog.Title>{toSentenceCase(lang.terms.REFER_AND_EARN_POINTS)}</Dialog.Title>
          <Dialog.Description>
            {toSentenceCase(lang.terms.REFER_AND_EARN_POINTS)}
          </Dialog.Description>
        </div>
        <div className="flex flex-col items-center gap-4">
          <UI.Avatar
            src={item.team.iconUrl ?? UI.toInitialsSVG(item.team.name)}
            variant="square"
            className="size-8"
          />
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col text-center gap-2">
              <h3 className="text-base">{toSentenceCase(lang.terms.REFERRAL_DIALOG_TITLE)}</h3>
              <p className="text-sm text-gray-11">
                {toSentenceCase(lang.terms.REFERRAL_DIALOG_DESCRIPTION)}
              </p>
            </div>
            <div className="grid grid-cols-2 w-full">
              <div className="flex flex-col items-center gap-2 bshadow-r bshadow-gray-3">
                <span className="text-xs uppercase tracking-upper">
                  {lang.terms.REFERRAL_PRICE}
                </span>
                <div className="flex items-center justify-center gap-2">
                  <UI.Avatar src={item.image} variant="square" className="size-[18px]" />
                  <span className="text-sm leading-4">
                    {toTemplate(lang.terms.REFERRAL_PRICE_OFF_DIALOG, {
                      discountPercentage: referrerData.discountPercentageDecimal.toString(),
                    })}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="text-xs uppercase tracking-upper">{lang.terms.YOU_WILL_EARN}</span>
                <div className="flex items-center gap-2">
                  <UI.Icon.Points className="bg-points" />
                  <span className="font-departure-mono text-[11px] leading-[14px] uppercase">
                    +{formatNumber(pointsQuantity)} {lang.terms.POINTS}
                  </span>
                </div>
              </div>
            </div>
            <UI.Button
              type="button"
              kind="solid"
              size="md"
              className="h-10 w-full"
              onClick={handleNativeShare}
            >
              <UI.Icon.Share />
              <span>{toSentenceCase(lang.terms.SHARE_UNIQUE_LINK)}</span>
            </UI.Button>
          </div>
          <UI.Button
            type="button"
            kind="ghost"
            size="md"
            className="h-10 w-full"
            onClick={copyToClipboard}
          >
            {isCopied ? <UI.Icon.Check /> : <UI.Icon.Copy />}
            <span>{toSentenceCase(lang.terms.COPY_LINK_TO_CLIPBOARD)}</span>
          </UI.Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  )
}

function calculatePointsQuantity(args: {
  productPriceAmount: string
  discountPercentageDecimal: number
  pointsPerUsdReferral: number
}) {
  const { productPriceAmount, discountPercentageDecimal, pointsPerUsdReferral } = args
  const priceAmount = Number(productPriceAmount)
  const discountAmount = priceAmount * (discountPercentageDecimal / 100)
  const priceAfterDiscount = priceAmount - discountAmount
  return Math.round(pointsPerUsdReferral * priceAfterDiscount)
}

function generateShareText(
  lang: ReturnType<typeof useLang>,
  username: string,
  productName: string,
  referrerData: Referrals.ReferrerData,
) {
  const title = toSentenceCase(lang.terms.REFERRAL_SHARE_TEXT_TITLE)
  const content = toTemplate(toSentenceCase(lang.terms.REFERRAL_SHARE_TEXT_CONTENT), {
    username: toSentenceCase(username),
    item: productName,
    discountPercentage: referrerData.discountPercentageDecimal.toString(),
  })
  return `${title}\n${content}\n`
}

type ReferrerDialogProps = {
  referrerData: Referrals.ReferrerData
  pointsValueMapping?: Points.PointsConfiguration["valueMapping"]
  shopifyProduct: ShopifyProductWithData
  item: {
    name: string
    image: string
    team: {
      iconUrl: string | null
      name: string
    }
  }
}
